import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import { PropertiesType } from "../types/index";

const BaseURL = import.meta.env.VITE_API_URL;

export const searchProperties = createAsyncThunk<any, any>(
  "properties/search",
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await ipponApi.get(
        `${BaseURL}/api/v1/properties?${queryParams.toString()}`
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during searching for properties"
      );
    }
  }
);

export const getPropertyNames = createAsyncThunk<any, any>(
  "properties/getName",
  async () => {
    try {
      const result = await ipponApi.get(`${BaseURL}/api/v1/property-names`);
      return result.data.data;
    } catch (error: any) {
      return error;
    }
  }
);

export const getBuildingTypesAndLandCategories = createAsyncThunk<any, any>(
  "properties/getBuildingTypesAndLandCategories",
  async (_, { rejectWithValue }) => {
    try {
      const result = await ipponApi.get(`${BaseURL}/api/v1/property-filters`);
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during fetching building types and land categories"
      );
    }
  }
);

export const createProperty = createAsyncThunk<any, any>(
  "properties/create",
  async (payload, { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(
        `${BaseURL}/api/v1/properties`,
        payload
      );
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during creating property"
      );
    }
  }
);

export const fetchPropertyDetail = createAsyncThunk<any, any>(
  "properties/detail",
  async (id, { rejectWithValue }) => {
    try {
      const result = await ipponApi.get(`${BaseURL}/api/v1/properties/${id}`);
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during getting detail of properties"
      );
    }
  }
);

export const updateProperty = createAsyncThunk<any, any>(
  "properties/update",
  async ({ id, uploadFormData }, { rejectWithValue }) => {
    try {
      const result = await ipponApi.put(
        `${BaseURL}/api/v1/properties/${id}`,
        uploadFormData
      );
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "An error occurred during updating detail of property"
      );
    }
  }
);

export const deleteProperty = createAsyncThunk<any, any>(
  "properties/delete",
  async (formattedDataToDelete, { rejectWithValue }) => {
    try {
      const result = await ipponApi.delete(`${BaseURL}/api/v1/properties`, {
        data: formattedDataToDelete,
      });
      return result.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "An error occurred during deleting properties"
      );
    }
  }
);

// Download Excel file for multiple properties
export const downloadPropertiesExcel = createAsyncThunk<any, any>(
  "properties/downloadPropertiesExcel",
  async (propertyIds: string[], { rejectWithValue }) => {
    try {
      const result = await ipponApi.post(
        `${BaseURL}/api/v1/properties/excel-download`,
        { property_ids: propertyIds }
        // responseType: "blob" を削除してJSONレスポンスを受け取る
      );

      // JSONレスポンスをそのまま返す
      return result.data;
    } catch (error: any) {
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        return rejectWithValue({
          message: error.response.data?.message || "ダウンロードに失敗しました",
          status: error.response.status,
        });
      } else if (error.request) {
        // Network error
        return rejectWithValue({
          message: "ネットワークエラーが発生しました",
          status: 0,
        });
      } else {
        // Other error
        return rejectWithValue({
          message: "ダウンロードに失敗しました",
          status: 0,
        });
      }
    }
  }
);

const initialState: PropertiesType = {
  new: {
    data: [],
    loading: false,
    error: false,
  },
  searched: {
    // data: {
    //   total: 0,
    //   page: 1,
    //   limit: 5,
    //   items: [],
    // },
    loading: false,
    error: false,
  },
  names: {
    data: [],
    loading: false,
    error: false,
  },
  detailed: {
    data: {},
    loading: false,
    error: false,
  },
  chartData: {
    data: [],
    loading: false,
    error: false,
  },
  filterOptions: {
    data: {
      building_types: [],
      land_categories: [],
    },
    loading: false,
    error: false,
  },
};

export const propertiesSlice = createSlice({
  name: "properties",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(searchProperties.fulfilled, (state, action) => {
      state.searched.data = action.payload;
      state.searched.loading = false;
    });
    builder.addCase(searchProperties.pending, (state) => {
      state.searched.loading = true;
      state.searched.error = false;
    });
    builder.addCase(searchProperties.rejected, (state) => {
      state.searched.loading = false;
      state.searched.error = true;
    });
    builder.addCase(getPropertyNames.fulfilled, (state, action) => {
      state.names.data = action.payload;
      state.names.loading = false;
    });
    builder.addCase(getPropertyNames.pending, (state) => {
      state.names.loading = true;
      state.names.error = false;
    });
    builder.addCase(getPropertyNames.rejected, (state) => {
      state.names.loading = false;
      state.names.error = true;
    });
    builder.addCase(
      getBuildingTypesAndLandCategories.fulfilled,
      (state, action) => {
        state.filterOptions.data = action.payload;
        state.filterOptions.loading = false;
      }
    );
    builder.addCase(getBuildingTypesAndLandCategories.pending, (state) => {
      state.filterOptions.loading = true;
      state.filterOptions.error = false;
    });
    builder.addCase(getBuildingTypesAndLandCategories.rejected, (state) => {
      state.filterOptions.loading = false;
      state.filterOptions.error = true;
    });
    builder.addCase(createProperty.fulfilled, (state, action) => {
      state.new.data = action.payload;
      state.new.loading = false;
    });
    builder.addCase(createProperty.pending, (state) => {
      state.new.loading = true;
      state.new.error = false;
    });
    builder.addCase(createProperty.rejected, (state) => {
      state.new.loading = false;
      state.new.error = true;
    });
    builder.addCase(fetchPropertyDetail.fulfilled, (state, action) => {
      state.detailed.data = action.payload;
      state.detailed.loading = false;
    });
    builder.addCase(fetchPropertyDetail.pending, (state) => {
      state.detailed.loading = true;
      state.detailed.error = false;
    });
    builder.addCase(updateProperty.fulfilled, (state, action) => {
      state.detailed.data = action.payload;
      state.detailed.loading = false;
    });
    builder.addCase(updateProperty.pending, (state) => {
      state.detailed.loading = true;
      state.detailed.error = false;
    });
    builder.addCase(updateProperty.rejected, (state) => {
      state.detailed.loading = false;
      state.detailed.error = true;
    });
    builder.addCase(deleteProperty.fulfilled, (state, action) => {
      state.detailed.data = action.payload;
      state.detailed.loading = false;
    });
    builder.addCase(deleteProperty.pending, (state) => {
      state.detailed.loading = true;
      state.detailed.error = false;
    });
    builder.addCase(deleteProperty.rejected, (state) => {
      state.detailed.data = {};
      state.detailed.loading = false;
      state.detailed.error = true;
    });
    // downloadPropertiesExcel handlers
    builder.addCase(downloadPropertiesExcel.fulfilled, () => {
      // No state changes needed for download
    });
    builder.addCase(downloadPropertiesExcel.pending, () => {
      // No state changes needed for download
    });
    builder.addCase(downloadPropertiesExcel.rejected, () => {
      // No state changes needed for download
    });
  },
});

export default propertiesSlice.reducer;
